// Phase 34 · 20b — the rules behind the audit trail.
//
// Two of them are load-bearing:
//
//   isFailure  is a FIELD (audit_logs.status), never a match on the label.
//              The FAILURES tab and the danger rule both read it, so they
//              cannot disagree.
//
//   actorType  is derived server-side (audit_logs.actor_type: user | system |
//              api_key | webhook) and never inferred here from a name or an
//              id. The machine colouring is the only thing separating "a
//              person decided this" from "a job ran"; if the component
//              guessed it, the distinction would be a lie.

import type { AuditLog } from '@services/audit.service';

export type BacktrackKind = 'ticket' | 'automation' | 'knowledge' | 'other';

/** The status enum is success | failure | error. Anything not success failed. */
export function isFailure(log: AuditLog): boolean {
  return log.status !== 'success';
}

/**
 * One classifier, used by both the row and the count above it. If the tab
 * strip folded resources differently from the table, a tab would say 7 over a
 * list of 5 and neither number could be trusted again.
 */
export function resourceKind(resource?: string | null): BacktrackKind {
  switch ((resource || '').toLowerCase()) {
    case 'ticket':
      return 'ticket';
    case 'workflow':
    case 'integration':
      return 'automation';
    case 'article':
      return 'knowledge';
    default:
      return 'other';
  }
}

export function eventKind(log: AuditLog): BacktrackKind {
  return resourceKind(log.resource);
}

/**
 * The server's per-resource tally, folded through the SAME classifier the rows
 * use, so the tab counts describe the whole filtered trail rather than the
 * page that happens to be loaded.
 */
export function summaryKindCounts(
  byResource: Record<string, number>
): Record<BacktrackKind, number> {
  const counts: Record<BacktrackKind, number> = {
    ticket: 0,
    automation: 0,
    knowledge: 0,
    other: 0,
  };
  for (const [resource, n] of Object.entries(byResource || {})) {
    if (typeof n !== 'number' || Number.isNaN(n)) continue;
    counts[resourceKind(resource)] += n;
  }
  return counts;
}

/**
 * Failures across the whole filtered trail. Reads the status field the same
 * way `isFailure` does — anything that is not success failed — so the FAILURES
 * tab and the rows under it can never disagree.
 */
export function summaryFailureCount(byStatus: Record<string, number>): number {
  return Object.entries(byStatus || {}).reduce(
    (sum, [status, n]) =>
      status === 'success' || typeof n !== 'number' || Number.isNaN(n) ? sum : sum + n,
    0
  );
}

/** Everything that is not a signed-in person is a machine. Server-derived. */
export function isMachineActor(log: AuditLog): boolean {
  return (log.actorType || 'user').toLowerCase() !== 'user';
}

/** The name in the actor column: the person, or what kind of machine it was. */
export function actorLabel(log: AuditLog): string {
  if (!isMachineActor(log)) {
    return log.userName || log.userEmail || 'Unknown user';
  }
  switch ((log.actorType || '').toLowerCase()) {
    case 'api_key':
      return 'API key';
    case 'webhook':
      return 'Webhook';
    default:
      return 'System';
  }
}

/** One bucket per actor for the rail; every machine folds into one row. */
export function actorKey(log: AuditLog): string {
  return isMachineActor(log) ? `machine:${actorLabel(log)}` : `user:${actorLabel(log)}`;
}

export function eventLabel(log: AuditLog, actionLabel: string): string {
  const resource = (log.resource || '').replace(/_/g, ' ');
  return resource ? `${actionLabel} ${resource}` : actionLabel;
}

function scalar(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return null;
  return String(value);
}

/**
 * `before → after`, and nothing else. No free-text reason suffix: a reason
 * captured at change time would earn its own column, not a tail on this one.
 * Returns null when the log carries no comparable scalar change — the row then
 * says so rather than inventing a transition.
 */
export function changeDetail(log: AuditLog, maxFields = 2): string | null {
  const fields = (log.changedFields || []).filter(Boolean);
  const candidates = fields.length
    ? fields
    : [...new Set([...Object.keys(log.newValue || {}), ...Object.keys(log.oldValue || {})])];

  const parts: string[] = [];
  for (const field of candidates) {
    const before = scalar(log.oldValue?.[field]);
    const after = scalar(log.newValue?.[field]);
    if (before === null && after === null) continue;
    parts.push(`${field}: ${before ?? '—'} → ${after ?? '—'}`);
    if (parts.length === maxFields) break;
  }
  return parts.length ? parts.join(' · ') : null;
}

const RECORD_ROUTE: Record<string, string> = {
  ticket: '/tickets',
  customer: '/customers',
  article: '/knowledge',
};

/** A record ref only exists when there is somewhere to go. */
export function recordRef(log: AuditLog): { label: string; href: string | null } | null {
  if (!log.resourceId) return null;
  const base = RECORD_ROUTE[(log.resource || '').toLowerCase()];
  return {
    label: `#${log.resourceId.slice(0, 8)}`,
    href: base ? `${base}/${log.resourceId}` : null,
  };
}

export function eventHour(log: AuditLog): number {
  const d = new Date(log.createdAt);
  return Number.isNaN(d.getTime()) ? -1 : d.getHours();
}

export function timeLabel(log: AuditLog): string {
  const d = new Date(log.createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export interface HourBucket {
  hour: number;
  count: number;
  failed: boolean;
}

/** Eight buckets ending at the latest hour that actually has an event. */
export function eventsByHour(logs: AuditLog[], span = 8): HourBucket[] {
  const hours = logs.map(eventHour).filter((h) => h >= 0);
  if (!hours.length) return [];
  const last = Math.max(...hours);
  const start = Math.max(0, last - (span - 1));
  const buckets: HourBucket[] = [];
  for (let h = start; h <= last; h++) buckets.push({ hour: h, count: 0, failed: false });
  for (const log of logs) {
    const bucket = buckets.find((b) => b.hour === eventHour(log));
    if (!bucket) continue;
    bucket.count++;
    if (isFailure(log)) bucket.failed = true;
  }
  return buckets;
}

export interface ActorTally {
  key: string;
  name: string;
  machine: boolean;
  count: number;
}

/** Who changed things — machines first, because that is the surprising row. */
export function tallyActors(logs: AuditLog[]): ActorTally[] {
  const tally = new Map<string, ActorTally>();
  for (const log of logs) {
    const key = actorKey(log);
    const prev = tally.get(key);
    tally.set(key, {
      key,
      name: actorLabel(log),
      machine: isMachineActor(log),
      count: (prev?.count ?? 0) + 1,
    });
  }
  return [...tally.values()].sort((a, b) => b.count - a.count);
}

/** The label a machine actor type is shown under. Mirrors `actorLabel`. */
function machineLabel(actorType: string): string {
  switch (actorType.toLowerCase()) {
    case 'api_key':
      return 'API key';
    case 'webhook':
      return 'Webhook';
    default:
      return 'System';
  }
}

/**
 * Who changed things, over the whole filtered trail. Machines are complete
 * (the server groups every actor_type); people are the server's top ten,
 * which is more rows than the rail shows.
 *
 * Machine types that share a label fold into one row, so "System" is one line
 * rather than two that a reader would have to add up.
 */
export function summaryActors(summary: {
  byActorType: Record<string, number>;
  topUsers: Array<{ userName?: string | null; userEmail?: string | null; count: number }>;
}): ActorTally[] {
  const rows = new Map<string, ActorTally>();

  for (const [actorType, count] of Object.entries(summary.byActorType || {})) {
    if (actorType.toLowerCase() === 'user') continue;
    if (typeof count !== 'number' || Number.isNaN(count)) continue;
    const name = machineLabel(actorType);
    const key = `machine:${name}`;
    const prev = rows.get(key);
    rows.set(key, { key, name, machine: true, count: (prev?.count ?? 0) + count });
  }

  for (const user of summary.topUsers || []) {
    if (typeof user.count !== 'number' || Number.isNaN(user.count)) continue;
    const name = user.userName || user.userEmail || 'Unknown user';
    const key = `user:${name}`;
    const prev = rows.get(key);
    rows.set(key, { key, name, machine: false, count: (prev?.count ?? 0) + user.count });
  }

  return [...rows.values()].sort((a, b) => b.count - a.count);
}

/**
 * The cause, not the code. A failures card that reads `SMTP 550` starts a
 * support ticket; one that names the thing that broke gets fixed in a minute —
 * so this leads with what failed and how often, and carries the service's own
 * message only as the second line.
 */
export function failureCause(
  failures: AuditLog[],
  actionLabel: (action: string) => string,
  /**
   * True when `failures` is only the loaded slice of a larger set. The tally
   * then says so instead of stating a whole-trail total it cannot see.
   */
  partial = false
): { headline: string; message: string | null } | null {
  if (!failures.length) return null;

  const counts = new Map<string, { n: number; log: AuditLog }>();
  for (const log of failures) {
    const key = `${log.action}:${log.resource}`;
    const prev = counts.get(key);
    counts.set(key, { n: (prev?.n ?? 0) + 1, log: prev?.log ?? log });
  }
  const worst = [...counts.values()].sort((a, b) => b.n - a.n)[0];
  const what = eventLabel(worst.log, actionLabel(worst.log.action)).toLowerCase();
  const subject = worst.log.resourceName ? ` on ${worst.log.resourceName}` : '';

  const where = partial ? ' in the events loaded here' : '';
  return {
    headline:
      worst.n === 1
        ? `${what}${subject} failed once${where}.`
        : `${what}${subject} failed ${worst.n} times${where}.`,
    message: worst.log.errorMessage || null,
  };
}
